import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { formatPrice, formatArea, formatUnit, extractMapSrc } from "../../utils/format";

const RealEstateDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_jwt");
    fetch(`/api/realestate?id=${encodeURIComponent(id)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((r) => {
        if (!r.ok) throw new Error("取得失敗");
        return r.json();
      })
      .then(setItem)
      .catch(() => setItem(null));
  }, [id]);

  // 削除
  const handleDelete = async () => {
    if (isDeleting) return; // 二重削除ガード
    if (!window.confirm("この物件を削除しますか？")) return;
    setIsDeleting(true);
    const token = localStorage.getItem("admin_jwt");
    try {
      const res = await fetch("/api/realestate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, delete: true }),
      });
      if (res.ok) {
        alert("削除しました");
        nav("/admin/realestate");
      } else {
        alert("削除に失敗しました");
      }
    } catch (err) {
      console.error(err);
      alert("エラーが発生しました");
    } finally {
      setIsDeleting(false);
    }
  };

  // 掲載/非公開の切替
  const handleTogglePublish = async () => {
    if (isToggling || !item) return;
    setIsToggling(true);
    const token = localStorage.getItem("admin_jwt");
    const nextPublished = item.published === false; // 非公開なら掲載へ
    try {
      const res = await fetch("/api/realestate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...item, id, published: nextPublished }),
      });
      if (res.ok) {
        setItem((prev) => ({ ...prev, published: nextPublished }));
      } else {
        alert("公開状態の変更に失敗しました");
      }
    } catch (err) {
      console.error(err);
      alert("エラーが発生しました");
    } finally {
      setIsToggling(false);
    }
  };

  if (!item) return <p className="p-6">読み込み中…</p>;

  const isPublished = item.published !== false;

  // 管理表示は全項目を表示（空欄は「—」）
  const specs = [
    { label: "販売価格", value: formatPrice(item.price, item.taxIncluded !== false) },
    { label: "表面利回り", value: formatUnit(item.surfaceYield, "%") },
    { label: "民泊稼働率", value: item.minpakuOccupancy },
    { label: "物件種別", value: item.propertyType },
    { label: "所在地", value: item.address },
    { label: "地番", value: item.chiban },
    { label: "住居表示", value: item.residenceDisplay },
    { label: "交通", value: item.transport },
    { label: "土地面積", value: formatArea(item.landArea) },
    { label: "建物面積", value: formatArea(item.buildingArea) },
    { label: "間取り", value: item.layout },
    { label: "戸数", value: item.units },
    { label: "構造", value: item.structure },
    { label: "設備", value: item.equipment },
    { label: "築年", value: item.buildYear },
    { label: "地目/権利", value: item.landCategory },
    { label: "用途地域", value: item.zoning },
    { label: "建ぺい率", value: formatUnit(item.buildingCoverage, "%") },
    { label: "容積率", value: formatUnit(item.floorAreaRatio, "%") },
    { label: "道路幅員", value: formatUnit(item.roadWidth, "m") },
    { label: "接道状況", value: item.roadAccess },
    { label: "防火規制", value: item.fireZone },
    { label: "高度地区", value: item.heightDistrict },
    { label: "都市計画道路", value: item.cityPlanRoad },
    { label: "他の法令上の制限", value: item.otherRestrictions },
    { label: "取引形態", value: item.transactionType },
    { label: "引渡日", value: item.handoverDate },
  ];

  return (
    <div className="adm-card max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-gray-800">{item.name}</h1>
        <span
          className={`text-xs px-2 py-0.5 rounded-full ${
            isPublished
              ? "bg-green-100 text-green-700"
              : "bg-gray-200 text-gray-600"
          }`}
        >
          {isPublished ? "掲載中" : "非公開"}
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        登録日：{" "}
        {item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("ja-JP", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : ""}
      </p>

      {specs.length > 0 && (
        <table className="w-full text-sm mb-4 border-t">
          <tbody>
            {specs.map((s) => (
              <tr key={s.label} className="border-b">
                <th className="text-left align-top py-2 pr-4 w-28 text-gray-600 font-medium">
                  {s.label}
                </th>
                <td className="py-2">{s.value || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {item.body && <div className="mb-4 whitespace-pre-wrap">{item.body}</div>}

      {item.photos?.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {item.photos.map((url) => (
            <img
              key={url}
              src={url}
              alt="物件写真"
              className="w-full object-cover"
            />
          ))}
        </div>
      )}

      {Array.isArray(item.detailFiles) && item.detailFiles.length > 0 && (
        <div className="mb-4">
          <h2 className="font-semibold">詳細資料</h2>
          <ul className="list-disc pl-5">
            {item.detailFiles.map((f, i) => (
              <li key={i}>
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {f.name || `資料${i + 1}.pdf`}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {item.inquiryUrl && (
        <div className="mb-4">
          <h2 className="font-semibold">お問い合わせリンク</h2>
          <a
            href={item.inquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            {item.inquiryUrl}
          </a>
        </div>
      )}

      {item.mapUrl && (
        <div className="mb-4">
          <h2 className="font-semibold mb-2">地図</h2>
          <iframe
            src={extractMapSrc(item.mapUrl)}
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="物件地図"
          />
        </div>
      )}

      <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
        <Link to={`/admin/realestate/${id}/edit`} className="adm-btn-warn">
          編集
        </Link>
        <button
          type="button"
          onClick={handleTogglePublish}
          disabled={isToggling}
          className={isPublished ? "adm-btn-sub" : "adm-btn"}
        >
          {isToggling ? "変更中…" : isPublished ? "非公開にする" : "掲載する"}
        </button>
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="adm-btn-danger"
        >
          {isDeleting ? "削除中…" : "削除"}
        </button>
        <Link
          to="/admin/realestate"
          className="ml-auto text-sm text-gray-600 hover:underline"
        >
          ← 一覧に戻る
        </Link>
      </div>
    </div>
  );
};

export default RealEstateDetail;
